(function () {
  "use strict";

  var PROPERTY_KEY = "_apa_checkout_session";
  var LIFECYCLE_KEY = "apa_checkout_session_lifecycle_v1";
  var FETCH_BYPASS = "__apaCheckoutSessionFetchBypass";
  var SESSION_TOKEN_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  var syncing = false;
  var initPromise = null;
  var currentState = { ok: false, ready: false, token: null };

  function rootPath() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function cartUrl(suffix) {
    return rootPath().replace(/\/?$/, "/") + suffix;
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    var bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = Array.from(bytes, function (b) {
      return b.toString(16).padStart(2, "0");
    }).join("");
    return (
      hex.slice(0, 8) + "-" +
      hex.slice(8, 12) + "-" +
      hex.slice(12, 16) + "-" +
      hex.slice(16, 20) + "-" +
      hex.slice(20)
    );
  }

  function normalizeToken(value) {
    var token = String(value || "").trim();
    return SESSION_TOKEN_RE.test(token) ? token.toLowerCase() : null;
  }

  function loadLifecycle() {
    try {
      var raw = window.localStorage.getItem(LIFECYCLE_KEY);
      if (!raw) return { had_shippable_cart: false, pending_rotation: false, provisional_token: null };
      var parsed = JSON.parse(raw);
      return {
        had_shippable_cart: Boolean(parsed.had_shippable_cart),
        pending_rotation: Boolean(parsed.pending_rotation),
        provisional_token: normalizeToken(parsed.provisional_token),
      };
    } catch (_err) {
      return { had_shippable_cart: false, pending_rotation: false, provisional_token: null };
    }
  }

  function saveLifecycle(state) {
    try {
      window.localStorage.setItem(
        LIFECYCLE_KEY,
        JSON.stringify({
          had_shippable_cart: Boolean(state.had_shippable_cart),
          pending_rotation: Boolean(state.pending_rotation),
          provisional_token: state.provisional_token || null,
        }),
      );
    } catch (_err) {
      /* quota / private mode */
    }
  }

  function lineNeedsSession(line) {
    return line && line.requires_shipping !== false;
  }

  function shippableLines(cart) {
    return (cart.items || []).filter(lineNeedsSession);
  }

  function lineSessionToken(line) {
    var props = line.properties || {};
    return String(props[PROPERTY_KEY] || "").trim();
  }

  function mergeLineProperties(properties, token) {
    var base = properties && typeof properties === "object" && !Array.isArray(properties)
      ? Object.assign({}, properties)
      : {};
    base[PROPERTY_KEY] = token;
    return base;
  }

  function distinctValidTokens(cart) {
    var tokens = shippableLines(cart)
      .map(function (line) { return normalizeToken(lineSessionToken(line)); })
      .filter(Boolean);
    var seen = {};
    var out = [];
    tokens.forEach(function (t) {
      if (!seen[t]) {
        seen[t] = true;
        out.push(t);
      }
    });
    return out;
  }

  function cartIsReady(cart, token) {
    var expected = normalizeToken(token);
    if (!expected) return false;
    var lines = shippableLines(cart);
    if (!lines.length) return true;
    for (var i = 0; i < lines.length; i++) {
      if (normalizeToken(lineSessionToken(lines[i])) !== expected) return false;
    }
    return true;
  }

  function resolveFromCart(cart, lifecycle) {
    var shippable = shippableLines(cart);
    var distinct = distinctValidTokens(cart);

    if (shippable.length > 0) {
      if (distinct.length > 1) {
        return { token: uuid(), needsRepair: true };
      }
      if (distinct.length === 1) {
        var authoritative = distinct[0];
        var needsRepair = shippable.some(function (line) {
          return normalizeToken(lineSessionToken(line)) !== authoritative;
        });
        return { token: authoritative, needsRepair: needsRepair };
      }
      var chosen = lifecycle.provisional_token || uuid();
      return { token: chosen, needsRepair: true };
    }

    if (lifecycle.had_shippable_cart) {
      lifecycle.pending_rotation = true;
      lifecycle.had_shippable_cart = false;
    }
    if (lifecycle.pending_rotation || !lifecycle.provisional_token) {
      lifecycle.provisional_token = uuid();
      lifecycle.pending_rotation = false;
    }
    return { token: lifecycle.provisional_token, needsRepair: false };
  }

  function findLineNeedingRepair(cart, token) {
    var expected = normalizeToken(token);
    if (!expected) return null;
    var lines = shippableLines(cart);
    for (var i = 0; i < lines.length; i++) {
      if (normalizeToken(lineSessionToken(lines[i])) !== expected) return lines[i];
    }
    return null;
  }

  function nativeFetch() {
    return (window.__apaCheckoutSessionNativeFetch || window.fetch).bind(window);
  }

  function internalFetch(url, init) {
    var options = init || {};
    options[FETCH_BYPASS] = true;
    return nativeFetch()(url, options);
  }

  async function fetchCartJson() {
    var res = await internalFetch(cartUrl("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("cart_fetch_failed:" + res.status);
    return res.json();
  }

  async function postCartChange(line, token) {
    var body = {
      id: line.key,
      quantity: line.quantity,
      properties: mergeLineProperties(line.properties, token),
    };
    var res = await internalFetch(cartUrl("cart/change.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return { ok: false, reason: "cart_change_failed:" + res.status };
    var cart = await res.json();
    return { ok: true, cart: cart };
  }

  async function repairCartSessions(cart, token) {
    var normalized = normalizeToken(token);
    if (!normalized) return { ok: false, reason: "checkout_session_token_invalid" };

    var current = cart;
    for (;;) {
      var target = findLineNeedingRepair(current, normalized);
      if (!target) break;
      var change = await postCartChange(target, normalized);
      if (!change.ok) return { ok: false, reason: change.reason || "cart_change_failed" };
      current = change.cart || await fetchCartJson();
    }

    if (!cartIsReady(current, normalized)) {
      return { ok: false, reason: "checkout_session_repair_incomplete", cart: current };
    }
    return { ok: true, token: normalized, cart: current };
  }

  async function reconcileCheckoutSession() {
    var lifecycle = loadLifecycle();
    var cart = await fetchCartJson();
    var shippable = shippableLines(cart);
    var resolution = resolveFromCart(cart, lifecycle);

    if (shippable.length > 0) {
      lifecycle.had_shippable_cart = true;
      lifecycle.provisional_token = resolution.token;
      lifecycle.pending_rotation = false;
    }
    saveLifecycle(lifecycle);

    if (!resolution.needsRepair) {
      var ready = cartIsReady(cart, resolution.token);
      return {
        ok: ready,
        ready: ready,
        token: resolution.token,
        cart: cart,
      };
    }

    var repaired = await repairCartSessions(cart, resolution.token);
    if (!repaired.ok) {
      return {
        ok: false,
        ready: false,
        token: resolution.token,
        cart: repaired.cart || cart,
        reason: repaired.reason,
      };
    }

    lifecycle = loadLifecycle();
    lifecycle.provisional_token = repaired.token;
    if (shippableLines(repaired.cart).length > 0) {
      lifecycle.had_shippable_cart = true;
      lifecycle.pending_rotation = false;
    }
    saveLifecycle(lifecycle);

    return {
      ok: true,
      ready: true,
      token: repaired.token,
      cart: repaired.cart,
    };
  }

  function getCurrentCheckoutSessionToken() {
    return currentState.token;
  }

  function whenCheckoutSessionReady() {
    if (!initPromise) {
      initPromise = reconcileCheckoutSession()
        .then(function (state) {
          currentState = state;
          return state;
        })
        .catch(function (err) {
          currentState = { ok: false, ready: false, token: null, reason: String(err) };
          return currentState;
        });
    }
    return initPromise;
  }

  function refreshCheckoutSessionReady() {
    initPromise = null;
    return whenCheckoutSessionReady();
  }

  function stampCheckoutSessionInputs(root, token) {
    var resolved = normalizeToken(token || getCurrentCheckoutSessionToken());
    if (!resolved) return null;
    (root || document).querySelectorAll("[data-apa-checkout-session-input]").forEach(function (input) {
      input.value = resolved;
    });
    return resolved;
  }

  function appendSessionToFormData(formData, token) {
    var resolved = normalizeToken(token || getCurrentCheckoutSessionToken());
    if (!resolved) return null;
    formData.set("properties[" + PROPERTY_KEY + "]", resolved);
    return resolved;
  }

  async function syncCartLineSessions() {
    if (syncing) return currentState;
    syncing = true;
    try {
      initPromise = null;
      var state = await whenCheckoutSessionReady();
      stampCheckoutSessionInputs(document, state.token);
      return state;
    } finally {
      syncing = false;
    }
  }

  function isGovernedPhysicalForm(form) {
    return Boolean(form && form.querySelector("[data-apa-checkout-session-input]"));
  }

  function isCartAddForm(form) {
    if (!form || String(form.tagName || "").toUpperCase() !== "FORM") return false;
    var action = String(form.getAttribute("action") || "");
    return action.indexOf("/cart/add") >= 0;
  }

  function applyLegacySubmitterFields(form, submitter) {
    if (!submitter || !submitter.getAttribute) return;
    var name = submitter.getAttribute("name");
    if (!name) return;
    var prior = form.querySelector("[data-apa-checkout-session-legacy-submitter='1']");
    if (prior) prior.parentNode.removeChild(prior);
    var input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = submitter.getAttribute("value") || "";
    input.setAttribute("data-apa-checkout-session-legacy-submitter", "1");
    form.appendChild(input);
  }

  function resubmitGovernedForm(form, submitter) {
    form.dataset.apaCheckoutSessionAllowSubmit = "1";
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit(submitter || undefined);
      return;
    }
    applyLegacySubmitterFields(form, submitter);
    form.submit();
  }

  function markCartAddPending(form, submitter) {
    if (form.dataset.apaCheckoutSessionPending === "1") return false;
    form.dataset.apaCheckoutSessionPending = "1";
    if (submitter && typeof submitter.setAttribute === "function") {
      submitter.setAttribute("data-pma-cart-add-busy", "true");
      submitter.setAttribute("aria-busy", "true");
      if (submitter.disabled !== true) {
        submitter.setAttribute("data-pma-cart-add-disabled-by-session", "true");
        submitter.disabled = true;
      }
    }
    return true;
  }

  function restoreCartAddPending(form, submitter) {
    form.dataset.apaCheckoutSessionPending = "0";
    if (!submitter || typeof submitter.removeAttribute !== "function") return;
    submitter.removeAttribute("data-pma-cart-add-busy");
    submitter.removeAttribute("aria-busy");
    if (submitter.getAttribute("data-pma-cart-add-disabled-by-session") === "true") {
      submitter.disabled = false;
      submitter.removeAttribute("data-pma-cart-add-disabled-by-session");
    }
  }

  function reportCartUpdateFailure() {
    var notices = window.pmaCheckoutPreparation;
    if (!notices || typeof notices.showNotice !== "function" || typeof notices.checkoutNoticeFor !== "function") return;
    notices.showNotice(notices.checkoutNoticeFor({ action: "block_session", added: false }));
  }

  var delegatedSubmitBound = false;
  function bindDelegatedGovernedSubmit() {
    if (delegatedSubmitBound) return;
    delegatedSubmitBound = true;
    document.addEventListener(
      "submit",
      function (event) {
        var form = event.target;
        if (!isCartAddForm(form) || !isGovernedPhysicalForm(form)) return;
        if (form.dataset.apaCheckoutSessionAllowSubmit === "1") {
          form.dataset.apaCheckoutSessionAllowSubmit = "0";
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        var submitter = event.submitter || null;
        if (!markCartAddPending(form, submitter)) return;
        refreshCheckoutSessionReady().then(function (state) {
          if (!state.ready || !state.token) {
            restoreCartAddPending(form, submitter);
            reportCartUpdateFailure();
            return;
          }
          stampCheckoutSessionInputs(form, state.token);
          restoreCartAddPending(form, submitter);
          resubmitGovernedForm(form, submitter);
        }).catch(function () {
          restoreCartAddPending(form, submitter);
          reportCartUpdateFailure();
        });
      },
      true,
    );
  }

  function shouldReconcileAfterFetch(url, init) {
    if (init && init[FETCH_BYPASS]) return false;
    if (!url || url.indexOf("/cart/") < 0) return false;
    return (
      url.indexOf("/cart/add") >= 0 ||
      url.indexOf("/cart/change") >= 0 ||
      url.indexOf("/cart/clear") >= 0
    );
  }

  function formDataRequiresCheckoutSession(formData) {
    return Boolean(formData && typeof formData.has === "function"
      && formData.has("properties[" + PROPERTY_KEY + "]"));
  }

  function patchFetch() {
    if (window.__apaCheckoutSessionFetchPatched) return;
    window.__apaCheckoutSessionFetchPatched = true;
    var original = window.fetch.bind(window);
    window.__apaCheckoutSessionNativeFetch = original;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var options = init || {};
      var run = function () {
        return original(input, options).then(function (response) {
          if (response.ok && shouldReconcileAfterFetch(url, options)) {
            initPromise = null;
            syncCartLineSessions();
          }
          return response;
        });
      };

      if (
        url.indexOf("/cart/add") >= 0
        && options.body instanceof FormData
        && !options[FETCH_BYPASS]
        && formDataRequiresCheckoutSession(options.body)
      ) {
        return refreshCheckoutSessionReady().then(function (state) {
          if (!state.ready || !state.token) {
            return new Response(
              JSON.stringify({ status: 422, message: "checkout_session_not_ready" }),
              { status: 422, headers: { "Content-Type": "application/json" } },
            );
          }
          appendSessionToFormData(options.body, state.token);
          return run();
        });
      }

      return run();
    };
  }

  function init(root) {
    bindDelegatedGovernedSubmit();
    patchFetch();
    whenCheckoutSessionReady().then(function (state) {
      if (state.token) stampCheckoutSessionInputs(root || document, state.token);
    });
  }

  window.PMA = window.PMA || {};
  window.PMA.checkoutSession = {
    PROPERTY_KEY: PROPERTY_KEY,
    whenReady: whenCheckoutSessionReady,
    refreshReady: refreshCheckoutSessionReady,
    getCurrentCheckoutSessionToken: getCurrentCheckoutSessionToken,
    stampCheckoutSessionInputs: stampCheckoutSessionInputs,
    syncCartLineSessions: syncCartLineSessions,
    reconcileCheckoutSession: reconcileCheckoutSession,
    init: init,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); });
  } else {
    init(document);
  }
})();
